import { useEffect, useMemo, useRef, useState, type MutableRefObject, type PointerEvent } from 'react';
import { Link, Navigate, useBlocker, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useWardrobe, usePutOutfit, wardrobeKeys, putOutfitKey, toOutfit,
  type ItemDto, type WardrobeState, type EquippedSlot,
} from '../api/wardrobe';
import { me, meKeys } from '../api/auth';
import { ApiClientError } from '../api/client';
import { Pet3DStage } from '../pet3d/Pet3DStage';
import { usePet3dStatus } from '../pet3d/loader';
import { useOnline } from '../pwa/useOnline';
import { ReconnectingBanner } from '../shell/ReconnectingBanner';
import { ScreenBackdrop } from '../shell/ScreenBackdrop';
import { Studio } from '../shell/Studio';
import { useLayoutTier, isStudio } from '../shell/layoutTiers';
import { useCompanions } from '../api/companions';
import { useOutfitDraft, equippedMapsEqual } from './useOutfitDraft';
import { SlotRail } from './SlotRail';
import { SLOT_GROUPS, slotCaption, type SlotGroupCode } from './slotGroups';
import { ItemDrawer, PANEL_COLS } from './ItemDrawer';
import { WornStrip, type WornPiece } from './WornStrip';
import { DyeSheet } from './DyeSheet';
import { SaveLookSheet } from './SaveLookSheet';
import { UnsavedChangesSheet } from './UnsavedChangesSheet';
import { LooksSheet } from '../looks/LooksSheet';
import { CompanionPickerSheet } from '../habitat/CompanionPickerSheet';
import { markLookSaved } from '../pwa/installPrompt';
import { stainsAllowed, useGorePref } from '../fx/gorePrefs';

// Mirrors WardrobeService.STAGE_ORDER (SlotRail keeps the same small copy).
const STAGE_ORDER = ['hatchling', 'juvenile', 'adult'];
function stageRank(stage: string): number {
  const i = STAGE_ORDER.indexOf(stage);
  return i < 0 ? 0 : i;
}

// Server rejection codes (GlobalExceptionHandler.java) mapped to the copy the brief specifies —
// unmapped/unknown codes (network failure, 500s) fall back to a generic message, same shape as
// screens/formErrors.ts's fallback.
const ERROR_MESSAGES: Record<string, string> = {
  not_owned: "You don't own that yet",
  slot_mismatch: 'That doesn’t fit this companion',
  stage_restricted: 'Unlocks at a later stage',
  dye_invalid: "That dye can't be used here",
};

function friendlyError(err: unknown): string {
  if (err instanceof ApiClientError && ERROR_MESSAGES[err.code]) return ERROR_MESSAGES[err.code];
  return 'Something went wrong — please try again.';
}

/**
 * Category dock (2026-09-14): the group the wardrobe opens on — the first group IN DOCK ORDER
 * that has at least one owned, fitting, unlocked piece, else Head.
 *
 * WHY NOT `groupOf(defaultActiveSlot(state))`, which is what this was until 2026-09-14. That
 * picked the lowest-zOrder populated SLOT and took its group, and zOrder is paint order, from
 * backdrop (0) upward — so the starter kit's Tail Ribbon (`tail_wrap`, zOrder 3) beat everything
 * a new player owns and the wardrobe's first screen was "Back & Tail" holding one ribbon. It was
 * not wrong so much as answering a question nobody asked: with 22 unlabelled slot plates, "which
 * plate is lit" barely read, and the drawer under it was the only thing that mattered. With five
 * labelled categories the lit plate is the first thing the eye lands on, and the dock reads
 * top-to-bottom, so the default has to agree with the order the player is looking at.
 *
 * The Phase 1 owner-gate rule is unchanged and is the whole reason the fallback is not simply
 * `'head'`: open on something POPULATED, never on "Nothing fits here yet" (seeded content has no
 * backdrop items, and backdrop used to be slot zero).
 */
function defaultActiveGroup(state: WardrobeState): SlotGroupCode {
  const rank = stageRank(state.stage);
  const unlocked = new Set(
    state.slots.filter((d) => rank >= stageRank(d.minStage)).map((d) => d.code),
  );
  const owns = (slotCode: string) =>
    unlocked.has(slotCode) && state.ownedItems.some((i) => i.slot === slotCode);
  return SLOT_GROUPS.find((g) => g.slots.some(owns))?.code ?? 'head';
}

/** Drag to turn (spec §4.5): a drag across the whole preview turns the pet half way round. */
export function yawFromDrag(dx: number, width: number): number {
  return width ? (dx / width) * Math.PI : 0;
}

/** A press becomes a turn only past this many px, so a tap on anything in the band stays a tap
 *  (the MapPanZoom lesson: capture taken on press retargets the click). */
const TURN_SLOP_PX = 4; // raw-ok: a pointer threshold, not a spacing step

/** Spec §3.4 focus-slot rule, the group-switch half: the group's first WORN (non-projected)
 *  slot in z-order, else null. The last-tapped half lives in `handleTapItem`. */
function firstWornSlotIn(
  group: SlotGroupCode, slots: WardrobeState['slots'], equipped: Record<string, EquippedSlot>,
): string | null {
  const members = new Set(SLOT_GROUPS.find((g) => g.code === group)?.slots ?? []);
  const sorted = [...slots].sort((a, b) => a.zOrder - b.zOrder);
  return sorted.find((s) => members.has(s.code) && equipped[s.code] && !equipped[s.code].projected)?.code ?? null;
}

export function WardrobeSkeleton() {
  return (
    <main className="wardrobe" aria-hidden="true">
      {/* Shape-matched to the real screen (§11.9 CLS): the preview band, then the drawer with the
          category strip as its first row — the skeleton has to put the strip where the real one
          is, or the whole drawer shifts at the loading→loaded transition. */}
      <div className="wardrobe-preview">
        <ScreenBackdrop id="bd_wardrobe_default" />
        <div className="compositor skeleton" />
      </div>
      <div className="item-drawer">
        <div className="slot-rail">
          {Array.from({ length: 5 }, (_, i) => <div key={i} className="skeleton slot-rail-skeleton-item" />)}
        </div>
        <div className="item-drawer-head"><div className="drawer-grab"><span className="drawer-grab-bar" /></div></div>
        <div className="item-drawer-scroll">
          <div className="skeleton skeleton-line" />
          <div className="skeleton skeleton-line" />
        </div>
      </div>
    </main>
  );
}

/**
 * SCREENS.md "/wardrobe/{companionId}" route entry point — parses/validates the `:companionId`
 * param and keys the actual screen on it (`key={companionId}` below), so a companion switch (an
 * in-place URL param change; React Router does not remount a matched route just because a param
 * changed) gets a genuinely fresh component instance — see `WardrobeScreenForCompanion`'s own
 * doc comment for why that matters (review findings I1/I2). M9: a non-numeric id
 * (`/wardrobe/abc`) has no possible wardrobe to fetch — redirect to Habitat instead of firing a
 * request that can only ever 404/500.
 */
export function WardrobeScreen() {
  const { companionId: companionIdParam } = useParams();
  // The TAB (`/wardrobe`, no param) resolves the active companion here rather than redirecting to
  // `/wardrobe/:id`. As a modal the redirect was harmless — a stub before a full-screen takeover.
  // As a paged tab it would slide a skeleton in and cut out of it a frame later, animating a page
  // that does not exist. This also supplies the name for the identity chip on BOTH paths, which a
  // deep link would otherwise have to fetch separately.
  const companions = useCompanions();
  const parsed = Number(companionIdParam);

  if (companionIdParam) {
    // M9: a non-numeric id (`/wardrobe/abc`) has no possible wardrobe to fetch — go to Habitat
    // rather than fire a request that can only ever 404/500.
    if (!Number.isFinite(parsed)) return <Navigate to="/habitat" replace />;
    const named = companions.data?.find((c) => c.id === parsed);
    return <WardrobeScreenForCompanion key={parsed} companionId={parsed} companionName={named?.name} />;
  }

  if (companions.isLoading) return <WardrobeSkeleton />;
  // A fetch error goes to Habitat, which already owns the "couldn't reach Project Pets" retry card
  // and the create-your-first-companion empty state — the same reasoning the deleted
  // WardrobeTabRedirect used, and the same reason neither is duplicated here.
  if (companions.isError) return <Navigate to="/habitat" replace />;
  const list = companions.data ?? [];
  const active = list.find((c) => c.isActive) ?? list[0];
  if (!active) return <Navigate to="/habitat" replace />;
  return <WardrobeScreenForCompanion key={active.id} companionId={active.id} companionName={active.name} />;
}

/**
 * SCREENS.md "/wardrobe/{companionId}" — §11.4, the hardest screen. Split view with locked
 * proportions: a never-scrolling ~55dvh live 3D preview, a horizontally scrolling slot
 * rail between (all 22 registry slots, filled/empty/locked-by-stage), and an independently
 * scrolling ~45% item drawer. Equip/unequip commits to the server immediately (optimistic
 * `usePutOutfit`) — there is no separate "save" step for an individual equip; `dirty` instead
 * tracks "unsaved as a Look" for Task 15's save flow, per useOutfitDraft's own header comment.
 *
 * Dye sheet (`DyeSheet`), Save Look (`SaveLookSheet`), and the
 * unsaved-changes navigation guard (`useBlocker`) are Task 15.
 */
export function WardrobeScreenForCompanion({
  companionId,
  companionName,
  onClose,
  closeRef,
}: {
  companionId: number;
  /** For the identity chip — the tab has nothing else on screen saying whose wardrobe this is. */
  companionName?: string;
  /** When set (habitat Dress overlay), Close dismisses the overlay instead of navigating. */
  onClose?: () => void;
  /** Embedded only (QA 2026-09-23 onboard F24): the overlay's Escape lands here as the SAME
   *  guarded close the Close button runs — the unsaved-Look guard included — via the
   *  BottomSheet's `onLockedEscape`. The fit editor keeps its own Escape (it closes itself). */
  closeRef?: MutableRefObject<(() => void) | null>;
}) {
  const isOnline = useOnline();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const wardrobe = useWardrobe(companionId);
  const putOutfit = usePutOutfit(companionId);
  /**
   * THE STUDIO (Task 10, spec §4.5). `studio` is every tier with a right slot (short / studio2 /
   * studio3): the item drawer — worn strip, Undo/Save Look's neighbours, the grid — becomes the
   * right panel and the preview takes the whole centre. `three` (studio3 only) also has a LEFT
   * panel, which takes the categories as a list plus the Looks entry; below it the strip stays
   * the drawer's own first row. On the phone nothing here changes: `Studio` renders only the
   * centre and every conditional below falls through to the layout that shipped.
   */
  const tier = useLayoutTier();
  const studio = isStudio(tier);
  const three = tier === 'studio3';
  // Task 11 (2026-08-23 battle-gore-and-wash): `WardrobeState` (useWardrobe above) has no
  // `stains` field — that lives on `CompanionDto`. `WardrobeScreen` (the route entry point above)
  // already calls `useCompanions()` for the identity chip / bare-`/wardrobe` resolution, so this
  // reuses the same cached query (same `companionsKeys.all` key) rather than adding a new fetch.
  const companions = useCompanions();
  // D13 (2026-08-24, battle-gore-and-wash fix round 1): gated on the VIEWING account's own age
  // band, same `=== 'ADULT'` pattern (folding `UNKNOWN` into the restricted side) as
  // `BattleScreen`'s/`ProfileScreen`'s own gates — see either for the fuller reasoning.
  // `meKeys.detail` is the shared `['me']` cache, so this is usually a cache hit.
  const meQuery = useQuery({ queryKey: meKeys.detail, queryFn: me, retry: false });
  // FINDING 1 fix (final whole-branch review): D10 requires the gore toggle to hide DRAWN stains,
  // not just the battle burst — this screen used to gate on age band alone. `stainsAllowed` folds
  // in both the age band and the battle-effects preference (server-backed per #14.2 / R1); see
  // its own definition for why this is the one shared derivation every render site must use.
  const [goreOn] = useGorePref();
  const stainsOn = stainsAllowed(goreOn, meQuery.data?.ageBand);
  const stains = stainsOn ? (companions.data?.find((c) => c.id === companionId)?.stains ?? []) : [];

  const draftFromStore = useOutfitDraft((s) => s.draft);
  const dirtyFromStore = useOutfitDraft((s) => s.dirty);
  const lastChangeFromStore = useOutfitDraft((s) => s.lastChange);

  // Category dock (2026-09-14): the dock selects a GROUP; Dye acts on one slot
  // inside it, the FOCUS slot. `focusSlotState` null means "derive from the group" (its first
  // worn slot, `firstWornSlotIn`); a string is the slot of the item the player last tapped here.
  const [activeGroupState, setActiveGroupState] = useState<SlotGroupCode | null>(null);
  const [focusSlotState, setFocusSlotState] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [showDyeSheet, setShowDyeSheet] = useState(false);
  const [showSaveSheet, setShowSaveSheet] = useState(false);
  const [showLooksSheet, setShowLooksSheet] = useState(false);
  const [embedGuard, setEmbedGuard] = useState(false);

  // Radix-migration (reviewer fix round, spec §3): refs for each sheet's own trigger button, so
  // BottomSheet's `restoreFocusRef` can return focus to the exact right place on close — see
  // BottomSheet.tsx's own doc comment on why this is needed (this app's sheets are all
  // externally controlled, not built on Dialog.Trigger, so Radix's own generic fallback isn't
  // reliable here). `closeButtonRef` also covers UnsavedChangesSheet, which has no single
  // natural trigger of its own (a blocked navigation can come from the browser's own back
  // gesture, with no DOM element at all) — the Close button is the most common real trigger.
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const dyeTriggerRef = useRef<HTMLButtonElement>(null);
  const saveLookTriggerRef = useRef<HTMLButtonElement>(null);
  const looksTriggerRef = useRef<HTMLButtonElement>(null);
  const whoTriggerRef = useRef<HTMLButtonElement>(null);
  /** The LEFT PANEL's Looks entry (studio3) has its own ref; `LooksSheet.restoreFocusRef` points
   *  at whichever of the two Looks triggers is mounted on this tier. */
  const panelLooksTriggerRef = useRef<HTMLButtonElement>(null);

  // The preview stage: drag to turn (spec §4.5). `yaw` is the dragged turn only; the idle sway is
  // the chunk's own (`sway` on the stage), so nothing here runs per frame.
  const [yaw, setYaw] = useState(0);
  const turn = useRef<{ id: number; x: number; from: number; dragging: boolean } | null>(null);
  function onTurnStart(e: PointerEvent<HTMLDivElement>) {
    turn.current = { id: e.pointerId, x: e.clientX, from: yaw, dragging: false };
  }
  function onTurnMove(e: PointerEvent<HTMLDivElement>) {
    const t = turn.current;
    if (!t || t.id !== e.pointerId) return;
    // No button held: the press ended off the stage (a mouse released outside within the slop never
    // sent us its pointerup). Drop it, or the next hover would turn the pet.
    if (e.buttons === 0) { turn.current = null; return; }
    const dx = e.clientX - t.x;
    if (!t.dragging) {
      if (Math.abs(dx) < TURN_SLOP_PX) return;
      t.dragging = true;
      // Only now: capture on press would retarget a tap's click to the stage.
      try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* the pointer is already gone */ }
    }
    setYaw(t.from + yawFromDrag(dx, e.currentTarget.offsetWidth));
  }
  function onTurnEnd() { turn.current = null; }
  const status = usePet3dStatus();

  /**
   * The item drawer's detent (§11.4, reworked 2026-08-20). Starts at PEEK: opening the wardrobe
   * should show you the companion you came to dress, at the largest size any layout here gives
   * it, with two rows of items already reachable. Expanding is one tap on the grab handle and
   * is where the filter chrome lives.
   *
   * Deliberately NOT persisted across mounts. The screen's whole job is the pet; a player who
   * expanded the drawer once to search for something should not have every future visit open
   * half-covered. Cheap to re-expand, and it keeps this state out of the session-storage
   * surface that `pp.world.view` occupies for a case that genuinely benefits from stickiness.
   */
  const [filtersOpen, setFiltersOpen] = useState(false);

  /**
   * I1/I2 fix (review): `useOutfitDraft` is a *module-level* store — it can carry a previous
   * companion's leftover contents (equipped map, `dirty`, `lastChange`) from before this
   * component instance existed. An earlier version of this screen reconciled that in a
   * `useEffect` keyed on `wardrobe.data` becoming available, which had two problems: (1) the
   * effect's reset landed one render *after* the one that already showed the (stale-until-then)
   * draft, an observable flash on every open, and (2) — the deeper issue — react-query notifies
   * query-state changes via a *real* `setTimeout(0)` (see `@tanstack/query-core`'s
   * `notifyManager`), not a microtask, so the render that finally had `wardrobe.data` could
   * already show fully correct content (draft computed as a local fallback below, same trick as
   * before) well *before* the effect that "officially" resets the store got a chance to run —
   * meaning a test's `waitFor`/`findBy` call could see correct DOM and close its `act()` scope
   * while that effect was still pending, so it fired genuinely unwrapped a tick later. Verified
   * empirically: a scratch repro warned even for a plain local `setActiveSlotState` click with
   * zero store interaction, tracing to this same lagging mount effect, not to anything
   * interaction-specific.
   *
   * The fix removes the effect entirely: `seededRef` tracks (per mount — remounts on
   * companionId, see `WardrobeScreen` above) whether THIS instance has ever written to the
   * store. Until it has, every render computes `draft`/`dirty`/`lastChange` straight from
   * `wardrobe.data` / defaults, ignoring whatever the store currently holds — always correct,
   * needs no state or effect. The store itself is seeded lazily, exactly once, inside
   * `handleTapItem`/`handleUndo` — an ordinary synchronous event handler, the one place a state
   * update is unconditionally safe and never "behind" what's already on screen.
   */
  const seededRef = useRef(false);
  // Memoized (Task 15 follow-up: activeEquippedItem below is the first consumer keying a memo
  // off `draft` itself) so its identity stays stable across renders that don't actually change
  // it — `wardrobe.data?.equipped ?? {}` would otherwise mint a fresh `{}` on every unseeded
  // render, defeating any memo downstream that depends on `draft`.
  const draft = useMemo(
    () => (seededRef.current ? draftFromStore : wardrobe.data?.equipped ?? {}),
    [draftFromStore, wardrobe.data],
  );
  const dirty = seededRef.current && dirtyFromStore;
  const lastChange = seededRef.current ? lastChangeFromStore : null;
  const activeGroup: SlotGroupCode | null =
    activeGroupState ?? (wardrobe.data ? defaultActiveGroup(wardrobe.data) : null);
  const activeGroupDef = activeGroup ? SLOT_GROUPS.find((g) => g.code === activeGroup) ?? null : null;
  const focusSlot: string | null = focusSlotState
    ?? (activeGroup && wardrobe.data ? firstWornSlotIn(activeGroup, wardrobe.data.slots, draft) : null);

  /**
   * Task 15 unsaved-changes guard: blocks any SPA navigation away from this screen — including
   * the Close button below, which needs no special-casing at all, since it navigates via the
   * same `nav(...)` (`useNavigate`) this data router also intercepts for every other navigation
   * attempt. `useBlocker` requires a *data* router (`createBrowserRouter`/`RouterProvider`,
   * which is what `routes.tsx`/`App.tsx` actually use) — WardrobeScreen.test.tsx's own render
   * harness was updated from a declarative `<MemoryRouter>` to `createMemoryRouter` for this
   * reason (see docs/RULINGS.md).
   */
  const blocker = useBlocker(dirty);

  function ensureSeeded() {
    if (!seededRef.current && wardrobe.data) {
      useOutfitDraft.getState().reset(wardrobe.data.equipped);
      seededRef.current = true;
    }
  }

  const itemNamesByCode = useMemo(() => {
    const map: Record<string, string> = {};
    for (const item of wardrobe.data?.ownedItems ?? []) map[item.code] = item.name;
    return map;
  }, [wardrobe.data]);

  const ownedItemsForActiveGroup = useMemo<ItemDto[]>(() => {
    if (!activeGroupDef || !wardrobe.data) return [];
    const { stage, slots } = wardrobe.data;
    const rank = stageRank(stage);
    const unlocked = new Set(slots.filter((s) => rank >= stageRank(s.minStage)).map((s) => s.code));
    const members = new Set(activeGroupDef.slots);
    return wardrobe.data.ownedItems.filter((i) => members.has(i.slot) && unlocked.has(i.slot));
  }, [activeGroupDef, wardrobe.data]);
  // Captions only when the group actually spans more than one SERVED slot for this companion —
  // a caption on every cell of a one-slot view would just restate the drawer's own title.
  const servedSlotsInGroup = useMemo(() => {
    if (!activeGroupDef || !wardrobe.data) return 0;
    const members = new Set(activeGroupDef.slots);
    return wardrobe.data.slots.filter((s) => members.has(s.code)).length;
  }, [activeGroupDef, wardrobe.data]);
  const captionFor = servedSlotsInGroup > 1 ? (item: ItemDto) => slotCaption(item.slot) : undefined;

  /** "Body · 3 pieces on" — the one thing the lit tab cannot say. Counts real pieces only, same
   *  exclusion of `projected` entries as the tab's own badge, so the two numbers always agree. */
  const drawerContextLine = useMemo(() => {
    if (!activeGroupDef) return 'Items';
    const n = activeGroupDef.slots.filter((slot) => draft[slot] && !draft[slot].projected).length;
    if (n === 0) return activeGroupDef.label;
    return `${activeGroupDef.label} · ${n} ${n === 1 ? 'piece' : 'pieces'} on`;
  }, [activeGroupDef, draft]);

  /**
   * Every worn piece across EVERY category, in dock order (finding 3, `WornStrip`). Deliberately
   * not scoped to the active group: the whole point is that the outfit was previously only
   * legible one category at a time. A `projected` entry (transmog) is excluded for the same
   * reason `SlotRail` excludes it — it is not a wardrobe piece, is not owned, and cannot be taken
   * off here. An entry whose item is not in `ownedItems` is skipped rather than rendered
   * nameless: the strip's whole job is to name things.
   */
  const wornPieces = useMemo<WornPiece[]>(() => {
    if (!wardrobe.data) return [];
    const byCode = new Map(wardrobe.data.ownedItems.map((i) => [i.code, i] as const));
    const pieces: WornPiece[] = [];
    for (const group of SLOT_GROUPS) {
      for (const slot of group.slots) {
        const worn = draft[slot];
        if (!worn || worn.projected) continue;
        const item = byCode.get(worn.itemCode);
        if (item) pieces.push({ slot, item });
      }
    }
    return pieces;
  }, [draft, wardrobe.data]);

  /** What an empty category holds, for the drawer's empty state (finding 6) — only the slots the
   *  SERVER serves for this companion, so a stage-locked slot is not advertised as something that
   *  belongs here yet. */
  const emptySlotCaptions = useMemo<string[]>(() => {
    if (!activeGroupDef || !wardrobe.data) return [];
    const served = new Set(wardrobe.data.slots.map((d) => d.code));
    return activeGroupDef.slots.filter((c) => served.has(c)).map(slotCaption);
  }, [activeGroupDef, wardrobe.data]);

  /** The strip's open chip IS the focus slot — the same slot Dye already acts on
   *  (spec §3.4). Two independent notions of "the piece being worked on" would drift apart the
   *  first time a player tapped a grid cell while a chip was open.
   *
   *  `null` means FOLLOW THE FOCUS SLOT, and that is what makes the verbs visible on arrival: the
   *  active category's worn piece is open before anything is tapped. The first cut shipped the
   *  row closed until a chip was pressed, and the owner's first report on it was "no option to
   *  resize" — the chip is a thumbnail with a caption, nothing about it says it hides Adjust, and
   *  the affordance it replaced had been on screen permanently since the free-transform wardrobe
   *  landed. A string is the chip the player pressed, which may be in another category. */
  const [openWornSlot, setOpenWornSlot] = useState<string | null>(null);
  const wornOpenSlot = openWornSlot ?? focusSlot;
  /** Finding 9 — the identity plate's picker. Only ever offered when there is more than one
   *  companion to pick between; one pet sees the plain label it always had. */
  const [showPickerSheet, setShowPickerSheet] = useState(false);
  const switchable = (companions.data?.length ?? 0) > 1;

  /**
   * Task 15's "Dye" affordance under the rail (SCREENS.md "dye-sheet-open") only shows when the
   * active slot's currently equipped item declares at least one dye channel. `draft` only ever
   * carries `itemCode` + the dye map already applied — `dyeChannels` (how many CAN be dyed)
   * lives on the item's own `ItemDto`, so this looks it up from `ownedItems` rather than
   * inventing/duplicating that count anywhere else.
   */
  const activeEquippedItem = useMemo<ItemDto | null>(() => {
    if (!focusSlot) return null;
    const equippedSlot = draft[focusSlot];
    if (!equippedSlot) return null;
    return wardrobe.data?.ownedItems.find((i) => i.code === equippedSlot.itemCode) ?? null;
  }, [focusSlot, draft, wardrobe.data]);

  /**
   * SCREENS.md drawer-search: "Drawer shows search results across all slots" — a non-empty
   * search overrides the slot filter entirely (ItemDrawer switches its base list to this one
   * whenever the search field isn't empty; see its own doc comment). Still scoped to "fits this
   * companion": the item's own slot must not be stage-restricted past the companion's current
   * stage — an owned `held_left` item while hatchling is excluded from search results entirely,
   * not shown locked-and-untappable, matching how it's simply absent from the active-slot list
   * were `held_left` itself selected (the rail already renders that slot locked with its own
   * hint; duplicating a second "locked" treatment inside search results would be redundant).
   */
  const ownedItemsAcrossSlots = useMemo<ItemDto[]>(() => {
    if (!wardrobe.data) return [];
    const { stage, slots, ownedItems } = wardrobe.data;
    const rank = stageRank(stage);
    const unlockedSlotCodes = new Set(
      slots.filter((def) => rank >= stageRank(def.minStage)).map((def) => def.code),
    );
    return ownedItems.filter((i) => unlockedSlotCodes.has(i.slot));
  }, [wardrobe.data]);

  // Latest-closure handle for the overlay's Escape (see `closeRef`); written in an effect, never
  // during render.
  useEffect(() => {
    if (closeRef) closeRef.current = handleClose;
  });

  // What the 3D pet wears: the draft (projections included), dyes resolved to hex.
  const outfit = useMemo(() => toOutfit(draft, wardrobe.data?.ownedDyes ?? []), [draft, wardrobe.data]);

  function handleClose() {
    if (onClose) {
      if (dirty) {
        setEmbedGuard(true);
        return;
      }
      onClose();
      return;
    }
    // C1 USED TO LIVE HERE: a full-screen route with no shell around it had no way out without
    // this button, so it walked SPA back-history and fell back to Habitat. The wardrobe is a tab
    // now (docs/RULINGS.md) — the dock is the way out, five ways over — and Close only renders for
    // the embedded `onClose` case handled above. Reaching here would mean a Close button with
    // nothing to close, so say so rather than navigate somewhere arbitrary.
    throw new Error('handleClose called with no onClose — the wardrobe tab has no Close button');
  }

  /**
   * C2 fix, part 2 (part 1 is usePutOutfit's `scope` in api/wardrobe.ts): commits via
   * `mutateAsync`, not `.mutate(vars, {onError, onSettled})`. Reading `@tanstack/query-core`'s
   * `MutationObserver.mutate()` shows it detaches its observer from whatever mutation was
   * previously in flight the instant a *new* `.mutate()`/`.mutateAsync()` call starts — so
   * per-call callbacks attached to an earlier, still-pending commit are silently **never
   * invoked** once a second commit begins before the first settles. That's a sharper version of
   * the "lost update" C2 described: not just cache stomping, but the first commit's own
   * rollback-resync-and-toast could fail to run at all. `mutateAsync`'s returned promise belongs
   * to that specific `execute()` call and settles independently of any later call on the same
   * hook instance, so awaiting it here is reliable regardless of what else happens to this
   * companion's shared `usePutOutfit` in the meantime.
   *
   * After it settles — config-level rollback (on error) and the now-*awaited* invalidateQueries
   * refetch have both already run, see usePutOutfit's comment — re-sync the local draft from
   * whatever the cache actually holds if it no longer matches, but only once no other commit for
   * this companion is still in flight (`queryClient.isMutating`, a live read of the mutation
   * cache — never a value captured in a closure, which could be stale by the time this async
   * function resumes). `scope` guarantees commits settle in send order, so the *latest* one's own
   * resync is always the authoritative last word; skipping an earlier commit's resync while a
   * later one is still pending avoids transiently clobbering that later commit's still-optimistic
   * local draft with this earlier commit's now-stale snapshot.
   */
  async function commitDraft(next: WardrobeState['equipped']) {
    try {
      await putOutfit.mutateAsync(next);
    } catch (err) {
      setToast(friendlyError(err));
    }
    if (queryClient.isMutating({ mutationKey: putOutfitKey(companionId) }) === 0) {
      const server = queryClient.getQueryData<WardrobeState>(wardrobeKeys.detail(companionId));
      const serverEquipped = server?.equipped ?? {};
      if (!equippedMapsEqual(useOutfitDraft.getState().draft, serverEquipped)) {
        useOutfitDraft.getState().reset(serverEquipped);
      }
    }
  }

  function handleTapItem(item: ItemDto) {
    // Keyed off the TAPPED ITEM's own slot, not the rail's currently active slot — a drawer-
    // search result (see ownedItemsAcrossSlots above) is very often from a different slot than
    // whatever the rail happens to have highlighted, and SCREENS.md is explicit: "tapping a
    // result equips it into its own slot." This is also simply correct for the non-searching
    // case too, since every visible item there already has item.slot inside the active group.
    if (!isOnline) return;
    ensureSeeded();
    // The last-tapped half of the focus-slot rule: Dye now follows this slot. An unequip
    // tap focuses it too — `activeEquippedItem` then resolves null and the triggers hide.
    setFocusSlotState(item.slot);
    const store = useOutfitDraft.getState();
    const current = store.draft[item.slot];
    if (current?.itemCode === item.code) store.unequip(item.slot);
    else store.equip(item.slot, item.code);
    void commitDraft(useOutfitDraft.getState().draft);
  }

  /** Opening a chip focuses its slot, so Dye acts on the piece the player just
   *  pointed at even when it lives in a category the dock is not showing. Pressing the open chip
   *  keeps it open: the chips choose WHICH piece the row acts on, they are not a disclosure that
   *  can put the screen back into the no-verb state the owner reported. */
  function handleWornToggle(slot: string) {
    setOpenWornSlot(slot);
    setFocusSlotState(slot);
  }

  function handleTakeOff(piece: WornPiece) {
    if (!isOnline) return;
    ensureSeeded();
    setFocusSlotState(piece.slot);
    useOutfitDraft.getState().unequip(piece.slot);
    void commitDraft(useOutfitDraft.getState().draft);
    // The chip this came from is about to stop existing; leaving the slot "open" would reopen the
    // actions the moment anything else was equipped into it.
    setOpenWornSlot(null);
  }

  function handleUndo() {
    if (!isOnline || !lastChange) return;
    ensureSeeded(); // defensive — undo is only ever offered once dirty, which implies already-seeded
    const store = useOutfitDraft.getState();
    if (!store.lastChange) return;
    store.undo();
    void commitDraft(useOutfitDraft.getState().draft);
  }

  // Task 15: dye sheet taps commit exactly like an equip/unequip tap above — same seed-then-
  // write-then-commit shape, just against `setDye`/`clearDye` instead of `equip`/`unequip`.
  function handleSetDye(slot: string, channel: string, dyeCode: string) {
    if (!isOnline) return;
    ensureSeeded();
    useOutfitDraft.getState().setDye(slot, channel, dyeCode);
    void commitDraft(useOutfitDraft.getState().draft);
  }

  function handleClearDye(slot: string, channel: string) {
    if (!isOnline) return;
    ensureSeeded();
    useOutfitDraft.getState().clearDye(slot, channel);
    void commitDraft(useOutfitDraft.getState().draft);
  }

  /**
   * Fires on a successful SaveLookSheet save (plain or overwrite), from either entry point — the
   * always-available dirty-bar button, or "Save" inside the unsaved-changes guard sheet below.
   * `reset(draft)` re-baselines the store at the CURRENT draft (unchanged values, just a fresh
   * baseline) rather than anything from the server: commits are already immediate, so there is
   * nothing to re-sync — this purely clears `dirty`/`lastChange`, exactly `reset`'s documented
   * job (see useOutfitDraft.ts's ruling on why there's no separate `commit()`/`rebaseline()`).
   */
  // SaveLookSheetProps.onSaved's type is `(look: LookDto) => void` — a handler that declares
  // fewer parameters is still a valid implementation of that callback type, and nothing here
  // (yet — see the Task 16 TODO below) needs the saved Look itself.
  function handleLookSaved() {
    useOutfitDraft.getState().reset(useOutfitDraft.getState().draft);
    setToast('Look saved');
    // §11.11 install-prompt trigger, half 1 of 2 (half 2 is markSession() at App boot) — the
    // player has now demonstrably dressed a pet and understands what installing means.
    markLookSaved();
    setShowSaveSheet(false);
    // The player's original navigation attempt (e.g. Close, tapped while dirty) is still
    // sitting blocked — now that it's no longer dirty, let it actually complete instead of
    // silently stranding them on a screen they already tried to leave.
    if (blocker.state === 'blocked') blocker.proceed();
  }

  /**
   * CRITICAL fix (reviewer finding): `LooksSheet`'s own "apply a Look" already committed
   * `slots` to the server (`putOutfit.mutateAsync`, awaited, success confirmed) by the time
   * this fires — but that alone never touches THIS screen's local draft store
   * (`useOutfitDraft`), which is what every subsequent equip/unequip tap actually reads and
   * writes (`handleTapItem`/`commitDraft` above). Left unfixed, the store would still hold
   * whatever the player had equipped BEFORE opening the Looks sheet, so the very next tap would
   * merge against that stale pre-apply draft instead of the Look's — silently reintroducing
   * slots the Look had just removed. `seededRef.current = true` first (same as `ensureSeeded`)
   * so the very next render reads `draftFromStore` (now holding the applied slots) instead of
   * falling back to `wardrobe.data?.equipped`.
   *
   * Same "same resync discipline as commitDraft" gate as `commitDraft`'s own post-settle resync
   * above: only rebaseline while no OTHER commit for this companion is still in flight
   * (`queryClient.isMutating`, scoped by `putOutfitKey` — the same scope key `usePutOutfit`
   * itself sets, which is also what makes LooksSheet's own `usePutOutfit(companionId)` call
   * serialize with this screen's commits in the first place, per-companion send order). Skipping
   * the rebaseline while something later is still pending avoids this apply's now-stale snapshot
   * clobbering that later, still-optimistic commit's own draft.
   */
  function handleLookApplied(slots: WardrobeState['equipped']) {
    seededRef.current = true;
    if (queryClient.isMutating({ mutationKey: putOutfitKey(companionId) }) === 0) {
      useOutfitDraft.getState().reset(slots);
    }
  }

  const Root = onClose ? 'div' : 'main';

  if (wardrobe.isLoading) return <WardrobeSkeleton />;

  if (wardrobe.isError) {
    // QA 2026-09-23 onboard F16: a companion the server does not know (`/wardrobe/999999`, or one
    // that is not yours — the same 404 either way) used to render an empty region with only the
    // dock. Route mode only: embedded, the Habitat already vouches for the id.
    if (!onClose && wardrobe.error instanceof ApiClientError && wardrobe.error.status === 404) {
      return (
        <Root className="wardrobe">
          <div className="retry-card">
            <p>That companion isn&rsquo;t in your den.</p>
            <Link to="/wardrobe" className="btn-primary">Back to the wardrobe</Link>
          </div>
        </Root>
      );
    }
    if (!isOnline) {
      // Nothing cached and offline: still an honest, non-blank state (§11.11) — same shape as
      // the "read-only offline" ready state, just with nothing to preview yet.
      return (
        <Root className="wardrobe">
          {onClose && (
            <button type="button" className="wardrobe-close" onClick={handleClose} aria-label="Close wardrobe">Close</button>
          )}
          <ReconnectingBanner />
        </Root>
      );
    }
    return (
      <Root className="wardrobe">
        {onClose && (
          <button type="button" className="wardrobe-close" onClick={handleClose} aria-label="Close wardrobe">Close</button>
        )}
        <div className="retry-card">
          <p>Couldn&rsquo;t reach Project Pets.</p>
          <button className="btn-primary" onClick={() => wardrobe.refetch()}>Try again</button>
        </div>
      </Root>
    );
  }

  const state = wardrobe.data;
  if (!state) return null; // enabled:false — route always supplies a valid companionId, defensive only

  /*
   * THE PIECES PLACED PER TIER (Task 10; the same lift-into-consts shape as HabitatScreen). Each
   * is built exactly once here and rendered in one place per tier, so the phone's tree is the
   * tree that shipped and the desk's is the same elements in the Studio's slots — never two copies
   * of a control competing for one ref.
   */
  // The category rail: the drawer's first row (strip) everywhere but studio3, where it is the left
  // panel as a list.
  const rail = (
    <SlotRail
      slotDefs={state.slots}
      equipped={draft}
      stage={state.stage}
      activeGroup={activeGroup}
      onSelectGroup={(g) => { setActiveGroupState(g); setFocusSlotState(null); setOpenWornSlot(null); }}
      itemNamesByCode={itemNamesByCode}
      orientation={three ? 'list' : 'strip'}
    />
  );
  // Undo / Save Look — the drawer's header actions on the phone, the `.wardrobe-context` row
  // under the preview on the desk. `undefined` while clean, so the drawer's header renders no
  // empty actions box.
  const actions = dirty ? (
    <>
      <span className="sr-only">Unsaved changes</span>
      {lastChange && (
        <button type="button" className="btn-quiet wardrobe-undo" disabled={!isOnline} onClick={handleUndo}>
          Undo
        </button>
      )}
      <button
        ref={saveLookTriggerRef}
        type="button"
        className="btn-primary wardrobe-save-look"
        onClick={() => setShowSaveSheet(true)}
      >
        Save Look
      </button>
    </>
  ) : undefined;
  // The drawer: under the preview on the phone/tablet, the right panel on the studio tiers. The
  // worn strip (Dye / Take off) stays inside it on every tier, so on the desk it is the top of the
  // right panel and `dyeTriggerRef` points at a mounted element wherever the drawer is.
  const drawer = (
    <ItemDrawer
      categories={three ? null : rail}
      items={ownedItemsForActiveGroup}
      allItems={ownedItemsAcrossSlots}
      equipped={draft}
      disabled={!isOnline}
      onTapItem={handleTapItem}
      title={drawerContextLine}
      captionFor={captionFor}
      emptySlotCaptions={emptySlotCaptions}
      wornStrip={(
        <WornStrip
          pieces={wornPieces}
          openSlot={wornOpenSlot}
          onToggle={handleWornToggle}
          onDye={(p) => { setFocusSlotState(p.slot); setShowDyeSheet(true); }}
          onTakeOff={handleTakeOff}
          disabled={!isOnline}
          dyeButtonRef={dyeTriggerRef}
        />
      )}
      filtersOpen={filtersOpen}
      onToggleFilters={() => setFiltersOpen((v) => !v)}
      actions={studio ? undefined : actions}
      columns={studio ? PANEL_COLS : undefined}
    />
  );

  return (
    <Studio
      className="wardrobe-studio"
      leftLabel="Categories"
      rightLabel="Fit & dye"
      left={three ? (
        <div className="wardrobe-categories-panel">
          {rail}
          {/* The desk's Looks entry — the same sheet the band's pill opens on every other tier.
              A quiet button in the flow of the panel rather than a floating pill: it is in a
              column of controls now, not over artwork. */}
          <button
            ref={panelLooksTriggerRef}
            type="button"
            className="btn-quiet wardrobe-panel-looks"
            onClick={() => setShowLooksSheet(true)}
          >
            Looks
          </button>
        </div>
      ) : undefined}
      right={studio ? drawer : undefined}
    >
    <Root className={studio ? 'wardrobe wardrobe-desk' : 'wardrobe'}>
      {!onClose && <h1 className="sr-only">Wardrobe</h1>}
      {!isOnline && <ReconnectingBanner />}

      <div className="wardrobe-preview">
        <ScreenBackdrop id="bd_wardrobe_default" />
        {/* As a TAB there is nothing to close — the dock is the way out (SCREENS.md "Wardrobe
            as a tab"). The slot the Close button occupied becomes the companion identity, which
            a tab needs and a modal did not: arriving from the dock, nothing else on screen says
            whose wardrobe this is. Close survives only for the embedded `onClose` usage, which
            genuinely is a modal. It is a LABEL, not a switcher — switching companions stays on
            Habitat, and a chip that looks like a control but is not one is worse than a plain
            one. */}
        {onClose ? (
          <button
            ref={closeButtonRef}
            type="button"
            className="wardrobe-close"
            onClick={handleClose}
            aria-label="Close wardrobe"
          >
            Close
          </button>
        ) : switchable ? (
          /* IT IS A CONTROL NOW (2026-09-14, finding 9). The comment above used to say the exact
             opposite — "a chip that looks like a control but is not one is worse than a plain
             one" — and was right at the time, because switching companions only existed on
             Habitat. Habitat grew a real picker the same week (`CompanionPickerSheet`), so the
             plate can be the same door here: identical plate, identical place, plus the chevron
             that has always been this app's mark for "this opens something".

             IT NAVIGATES, IT DOES NOT SET ACTIVE. Habitat's picker calls `PUT /companions/{id}/
             active` because Habitat IS the den — the question there is who is in the room. Here
             the question is only whose wardrobe to open, and `/wardrobe/:id` already answers it
             with no server call and no side effect on the rest of the game. A player dressing a
             second pet does not thereby move it into the den. */
          <>
            <button
              ref={whoTriggerRef}
              type="button"
              className="wardrobe-who wardrobe-who-btn"
              aria-haspopup="dialog"
              onClick={() => setShowPickerSheet(true)}
            >
              {companionName ?? 'Wardrobe'}
              <svg className="wardrobe-who-chevron" viewBox="0 0 24 24" aria-hidden="true" focusable="false"
                fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
          </>
        ) : (
          <p className="wardrobe-who">{companionName ?? 'Wardrobe'}</p>
        )}
        {/* Not at studio3: the left panel carries the one Looks entry there (see
            `panelLooksTriggerRef`), and two doors to the same sheet on one screen would only
            raise the question of which is which. Every other tier keeps this pill exactly. */}
        {!three && (
          <button
            ref={looksTriggerRef}
            type="button"
            className="wardrobe-looks-trigger"
            onClick={() => setShowLooksSheet(true)}
          >
            Looks
          </button>
        )}
        {/* THE STAGE: the live, dressed 3D pet (spec §4.4–4.5). Drag turns it; idle, it sways
            (in the chunk's loop, off under reduced motion). No pan or zoom — drag is the turn — and
            `frame="fit"` frames the pet's own height plus the tallest hat so it fills the band
            (RULINGS "3D characters — wardrobe"). Without 3D the PNG stays, undressed, and one quiet
            line says why. */}
        <div
          className="wardrobe-preview-stage"
          data-testid="wardrobe-preview-stage"
          onPointerDown={onTurnStart}
          onPointerMove={onTurnMove}
          onPointerUp={onTurnEnd}
          onPointerCancel={onTurnEnd}
        >
          <Pet3DStage
            species={state.speciesCode} stage={state.stage} variant="base"
            alt={status === 'unavailable' ? (companionName ?? 'Your pet') : `${companionName ?? 'Your pet'} dressed`}
            motion="idle" facing="right" stains={stains} stainOpacity={1} pokeClass={null} pokeGen={0}
            outfit={outfit} yaw={yaw} sway frame="fit"
          />
          {status === 'unavailable' && <p className="wardrobe-no-3d">{"This device can't show outfits in 3D."}</p>}
        </div>

        {/* THE CATEGORY STRIP USED TO BE HERE, as a vertical dock over the band's right margin
            (2026-08-20 → 2026-09-14). It is the drawer's first row now — see `SlotRail.tsx`. */}
      </div>

      {/* THE DESK'S CONTEXT ROW (Task 10): Undo / Save Look under the preview, in flow. On the
          phone they are the drawer's header actions (`actions` below), which on the desk would put
          the outfit's one consequence at the top of a side panel, away from the pet it is about.
          It is a sibling of the band, not a child: nothing floats over the pet.

          ALWAYS RENDERED on a studio tier, at a fixed height (fix round 1). The first cut mounted
          it only while dirty, and as a sibling of a `flex: 1 1 auto` band that made the band — and
          the pet — shrink 876 → 810 on the very tap that equipped something, then grow back on
          Undo: exactly what `.wardrobe-preview`'s own rule ("states its own height once and never
          re-solves … the pet never resizes mid-use") forbids. Now the row is there while clean
          too, carrying a quiet line, and `.wardrobe-context`'s `min-height` matches the actions
          row, so the band's height is the same on both sides of the toggle. */}
      {studio && (
        <div className="wardrobe-context">
          {dirty ? actions : <span className="wardrobe-context-quiet">No unsaved changes</span>}
        </div>
      )}

      {studio ? null : drawer}

      {/* The dirty bar is GONE as a floating overlay — Undo / Save Look are now the item
          drawer's own header actions (see the `actions` prop above). That deletes a whole class
          of defect this file and base.css both carry scar tissue for: the bar was absolutely
          positioned across the preview's top control row, squeezed between Close and Looks at
          390 CSS px, and needed `.wardrobe-dirty` on <main> purely to reserve its height inside
          `.wardrobe-preview` so the composite never rendered underneath it. An earlier revision
          anchored it to the wardrobe's BOTTOM instead and it landed squarely on the drawer's only
          visible item row — genuinely unclickable, and invisible to jsdom. Living inside the
          sheet, it has a real place in the layout and can overlap nothing. */}
      {/* The error toast keeps the OLD Task 18 anchor (full-width, below the top row) rather
          than squeezing into the dirty bar's narrow inset stack above — it needs the width for
          its message text, and (see commitDraft) a toast-producing commit's own resync clears
          `dirty` in the same tick in the common case, so `.wardrobe-dirty`'s preview reservation
          usually isn't there to protect this anchor anyway; this is unchanged from the tested
          Task 18 placement. */}
      {toast && (
        <div className="wardrobe-toast-stack">
          <div className="toast" role="alert">
            <span>{toast}</span>
            <button type="button" className="toast-dismiss" onClick={() => setToast(null)}>Dismiss</button>
          </div>
        </div>
      )}

      {showDyeSheet && focusSlot && activeEquippedItem && (
        <DyeSheet
          itemName={activeEquippedItem.name}
          dyeChannels={activeEquippedItem.dyeChannels}
          dye={draft[focusSlot]?.dye ?? {}}
          ownedDyes={state.ownedDyes}
          onSelect={(channel, dyeCode) => handleSetDye(focusSlot, channel, dyeCode)}
          onClear={(channel) => handleClearDye(focusSlot, channel)}
          onClose={() => setShowDyeSheet(false)}
          restoreFocusRef={dyeTriggerRef}
        />
      )}

      {showSaveSheet && (
        <SaveLookSheet
          companionId={companionId}
          draft={draft}
          onClose={() => setShowSaveSheet(false)}
          onSaved={handleLookSaved}
          restoreFocusRef={saveLookTriggerRef}
        />
      )}

      {showPickerSheet && (
        <CompanionPickerSheet
          activeId={companionId}
          onClose={() => setShowPickerSheet(false)}
          onPick={(c) => { setShowPickerSheet(false); navigate(`/wardrobe/${c.id}`); }}
          currentLabel="Dressing now"
          restoreFocusRef={whoTriggerRef}
        />
      )}

      {showLooksSheet && (
        <LooksSheet
          companionId={companionId}
          onClose={() => setShowLooksSheet(false)}
          onApplied={handleLookApplied}
          restoreFocusRef={three ? panelLooksTriggerRef : looksTriggerRef}
          onSaveCurrent={() => { setShowLooksSheet(false); setShowSaveSheet(true); }}
        />
      )}

      {/* Task 15 unsaved-changes guard — shown only while a navigation attempt (Close included)
          is actually blocked; hidden while SaveLookSheet is open on top of it so the two sheets
          never stack visually (SaveLookSheet's own onClose reveals this one again, since the
          blocker itself is untouched by simply closing that sheet without saving). */}
      {(blocker.state === 'blocked' || embedGuard) && !showSaveSheet && (
        <UnsavedChangesSheet
          onSave={() => setShowSaveSheet(true)}
          onDiscard={() => {
            if (wardrobe.data) useOutfitDraft.getState().reset(wardrobe.data.equipped);
            setEmbedGuard(false);
            if (blocker.state === 'blocked') {
              blocker.proceed();
              return;
            }
            onClose?.();
          }}
          onKeepEditing={() => {
            setEmbedGuard(false);
            if (blocker.state === 'blocked') blocker.reset();
          }}
          restoreFocusRef={closeButtonRef}
        />
      )}
    </Root>
    </Studio>
  );
}

// Route module surface for the lazy full-screen /wardrobe/:companionId route (routes.tsx) —
// mirrors NewCompanionScreen's `Component` export convention for React Router's `lazy()`.
export { WardrobeScreen as Component };
